<template>
  <section class="ren-decoration-cp ren-decoration-cp-ad-grid" :class="layoutClass" :style="gridStyle">
    <article v-for="(item, index) in items" :key="itemKey(item, index)">
      <img v-if="itemImage(item)" :src="itemImage(item)" :alt="itemTitle(item, index)" />
      <strong>{{ itemTitle(item, index) }}</strong>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  previewBoundString,
  previewRecordsFor,
  recordList,
  stringValue
} from '../../../previews/preview-helpers'
import { widgetMediaUrl } from '../../media'
import type { WidgetPreviewProps } from '../../types'

const LAYOUTS = ['SINGLE', 'TWO_COLUMNS', 'TOP_ONE_BOTTOM_TWO', 'LEFT_ONE_RIGHT_TWO', 'GRID_2X2'] as const

const props = defineProps<WidgetPreviewProps>()

const layoutMode = computed(() => {
  const value = String(props.component.props?.layoutMode ?? 'SINGLE').toUpperCase()
  return LAYOUTS.includes(value as (typeof LAYOUTS)[number]) ? value : 'SINGLE'
})
const layoutClass = computed(() => `layout-${layoutMode.value.toLowerCase().replaceAll('_', '-')}`)
const slotCount = computed(() => {
  if (layoutMode.value === 'SINGLE') return 1
  if (layoutMode.value === 'TWO_COLUMNS') return 2
  if (layoutMode.value === 'GRID_2X2') return 4
  return 3
})
const items = computed(() => {
  const records = previewRecordsFor(props.component, props.componentData, recordList(props.component.props?.items))
  const source = records.length ? records : [{ title: '广告位' }]
  return source.slice(0, slotCount.value)
})
const gridStyle = computed(() => {
  const gap = Number(props.component.style?.gap)
  const height = Number(props.component.style?.height)
  const radius = Number(props.component.style?.radius)
  return {
    gap: Number.isFinite(gap) && gap >= 0 ? `${gap}px` : undefined,
    minHeight: Number.isFinite(height) && height > 0 ? `${height}px` : undefined,
    borderRadius: Number.isFinite(radius) && radius >= 0 ? `${radius}px` : undefined
  }
})

function itemTitle(item: Record<string, unknown>, index: number) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.title',
    stringValue(item.title ?? item.label, `广告 ${index + 1}`),
    item
  )
}

function itemImage(item: Record<string, unknown>) {
  return widgetMediaUrl(
    previewBoundString(
      props.component,
      props.componentData,
      'item.imageUrl',
      stringValue(item.imageUrl ?? item.image),
      item
    )
  )
}

function itemKey(item: Record<string, unknown>, index: number) {
  return stringValue(item.id ?? item.code, `${itemTitle(item, index)}-${index}`)
}
</script>

<style scoped>
.ren-decoration-cp-ad-grid {
  display: grid;
  gap: 8px;
  overflow: hidden;
}

.ren-decoration-cp-ad-grid article {
  min-width: 0;
  display: grid;
  align-content: end;
  min-height: 72px;
  overflow: hidden;
  background: var(--ren-theme-background);
  color: var(--ren-theme-text);
}

.ren-decoration-cp-ad-grid img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ren-decoration-cp-ad-grid strong {
  padding: 8px;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.layout-single {
  grid-template-columns: 1fr;
}

.layout-two-columns,
.layout-grid-2x2 {
  grid-template-columns: 1fr 1fr;
}

.layout-top-one-bottom-two {
  grid-template-columns: 1fr 1fr;
}

.layout-top-one-bottom-two article:first-child {
  grid-column: 1 / -1;
}

.layout-left-one-right-two {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.layout-left-one-right-two article:first-child {
  grid-row: 1 / span 2;
}
</style>
